<template>
  <div class="w-full overflow-x-auto">
    <table class="w-full text-sm text-ink border-collapse">
      <thead>
        <tr class="border-b border-line">
          <th
            v-if="selectable"
            class="w-10 px-4 py-3 text-left">
            <input
              type="checkbox"
              :checked="allSelected"
              class="rounded border-line text-brand focus:ring-brand/30"
              @change="toggleAll" />
          </th>
          <th
            v-for="col in columns"
            :key="col.key"
            :class="[
              'px-4 py-3 text-left text-xs font-semibold text-muted uppercase tracking-wider whitespace-nowrap',
              col.sortable ? 'cursor-pointer select-none hover:text-ink' : '',
              col.align === 'right' ? 'text-right' : '',
              col.align === 'center' ? 'text-center' : '',
            ]"
            @click="col.sortable && toggleSort(col.key)">
            <span class="inline-flex items-center gap-1">
              {{ col.label }}
              <ChevronUp
                v-if="col.sortable && sortKey === col.key && sortDir === 'asc'"
                class="w-3 h-3 text-brand" />
              <ChevronDown
                v-else-if="col.sortable && sortKey === col.key && sortDir === 'desc'"
                class="w-3 h-3 text-brand" />
              <ChevronsUpDown
                v-else-if="col.sortable"
                class="w-3 h-3 opacity-30" />
            </span>
          </th>
        </tr>
      </thead>
      <tbody>
        <template v-if="sortedRows.length">
          <tr
            v-for="(row, idx) in paginatedRows"
            :key="row.id ?? idx"
            :class="[
              'border-b border-line/60 transition-colors',
              hover ? 'hover:bg-surfaceAlt cursor-pointer' : '',
              isSelected(row) ? 'bg-brandSoft/40' : '',
            ]"
            @click="hover && $emit('row-click', row)">
            <td
              v-if="selectable"
              class="px-4 py-3"
              @click.stop>
              <input
                type="checkbox"
                :checked="isSelected(row)"
                class="rounded border-line text-brand focus:ring-brand/30"
                @change="toggleRow(row)" />
            </td>
            <td
              v-for="col in columns"
              :key="col.key"
              :class="[
                'px-4 py-3',
                col.mono ? 'font-mono tabular-nums' : '',
                col.align === 'right' ? 'text-right' : '',
                col.align === 'center' ? 'text-center' : '',
              ]">
              <slot
                :name="`cell-${col.key}`"
                :row="row"
                :value="row[col.key]">
                {{ row[col.key] }}
              </slot>
            </td>
          </tr>
        </template>
        <tr v-else>
          <td
            :colspan="columns.length + (selectable ? 1 : 0)"
            class="px-4 py-10 text-center text-muted">
            <slot name="empty">
              <p class="text-sm">Sin registros</p>
            </slot>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Paginación -->
    <div
      v-if="paginate && sortedRows.length > pageSize"
      class="flex items-center justify-between px-4 py-3 border-t border-line">
      <p class="text-xs text-muted">
        Mostrando {{ pageStart + 1 }}-{{ Math.min(pageStart + pageSize, sortedRows.length) }} de {{ sortedRows.length }} registros
      </p>
      <div class="flex items-center gap-1">
        <button
          :disabled="currentPage === 1"
          class="p-1.5 rounded text-muted hover:text-ink hover:bg-surfaceAlt disabled:opacity-40 transition-colors"
          @click="currentPage--">
          <ChevronLeft class="w-4 h-4" />
        </button>
        <span class="px-2 text-sm font-medium text-ink">{{ currentPage }}</span>
        <button
          :disabled="currentPage >= totalPages"
          class="p-1.5 rounded text-muted hover:text-ink hover:bg-surfaceAlt disabled:opacity-40 transition-colors"
          @click="currentPage++">
          <ChevronRight class="w-4 h-4" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight } from '@lucide/vue'

export default {
  name: 'AppTable',
  components: { ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight },
  props: {
    columns: { type: Array, default: () => [] },
    rows: { type: Array, default: () => [] },
    hover: { type: Boolean, default: true },
    selectable: { type: Boolean, default: false },
    paginate: { type: Boolean, default: false },
    pageSize: { type: Number, default: 10 },
  },
  emits: ['row-click', 'selection-change'],
  data() {
    return {
      sortKey: '',
      sortDir: 'asc',
      currentPage: 1,
      selected: [],
    }
  },
  computed: {
    sortedRows() {
      if (!this.sortKey) return this.rows
      return [...this.rows].sort((a, b) => {
        const va = a[this.sortKey],
          vb = b[this.sortKey]
        const cmp = va < vb ? -1 : va > vb ? 1 : 0
        return this.sortDir === 'asc' ? cmp : -cmp
      })
    },
    totalPages() {
      return Math.ceil(this.sortedRows.length / this.pageSize)
    },
    pageStart() {
      return (this.currentPage - 1) * this.pageSize
    },
    paginatedRows() {
      if (!this.paginate) return this.sortedRows
      return this.sortedRows.slice(this.pageStart, this.pageStart + this.pageSize)
    },
    allSelected() {
      return this.rows.length > 0 && this.selected.length === this.rows.length
    },
  },
  methods: {
    toggleSort(key) {
      if (this.sortKey === key) this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      else {
        this.sortKey = key
        this.sortDir = 'asc'
      }
      this.currentPage = 1
    },
    isSelected(row) {
      return this.selected.includes(row)
    },
    toggleRow(row) {
      const idx = this.selected.indexOf(row)
      if (idx >= 0) this.selected.splice(idx, 1)
      else this.selected.push(row)
      this.$emit('selection-change', this.selected)
    },
    toggleAll() {
      this.selected = this.allSelected ? [] : [...this.rows]
      this.$emit('selection-change', this.selected)
    },
  },
}
</script>
